import { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Container, Row, Col } from 'reactstrap'
import s from './CreateCompanyModal.scss'
import { FaAngleRight, FaAngleDown } from 'react-icons/fa'
import {
  KeyValuePairs,
  InputContainer,
  GoogleAutocomplete,
  Checkbox,
  DatePicker
} from 'components/ideal-core-ui'
import { useImmer } from 'use-immer'
import { AutosuggestInput } from 'components/common/AutosuggestInput'
import Select from 'react-select'
import { makeGetRequest } from 'utils/api'
import { toSelectValues } from './serializers'
import { fromConstantPropertyToSelectOptions } from 'utils/serializers'
import { APP_CONSTANTS } from 'utils/constants'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { timezoneDate, utcDate } from 'utils/helpers'

const CreateCompanyModal = ({
  toggleCreateCompanyModal,
  isCreateCompanyModalOpen,
  lead,
  createCompany
}) => {
  const [isInfoSectionOpen, setIsInfoSectionOpen] = useState(true)
  const [isCreateCompanySectionOpen, setIsCreateCompanySectionOpen] = useState(true)
  const [prevLead, setPrevLead] = useState(lead)
  const [localLead, setLocalLead] = useImmer(lead)
  const [shouldCopyNotes, setShouldCopyNotes] = useState(true)
  const [shouldCopyChecklist, setShouldCopyChecklist] = useState(true)
  const [shouldCopySpend, setShouldCopySpend] = useState(true)
  const [companyName, setCompanyName] = useState(lead.company || '')
  const [companyLegalName, setCompanyLegalName] = useState(lead.company)
  const [companyDescription, setCompanyDescription] = useState(lead.about)
  const [hasDuplicateError, setHasDuplicateError] = useState(false)
  const [companyTypes, setCompanyTypes] = useState([])
  const [segments, setSegments] = useState([])
  const [types, setTypes] = useState([])
  const [size, setSize] = useState(null)
  const [segment, setSegment] = useState(null)
  const [parentCompany, setParentCompany] = useState(null)
  const [preQualified, setPreQualified] = useState(false)
  const [preQualifiedExpDate, setPreQualifiedExpDate] = useState('')

  if (prevLead !== lead) {
    setPrevLead(lead)
    setLocalLead(lead)
  }

  useEffect(() => {
    ;(async () => {
      const segments = await makeGetRequest('/v1/market-segments')
      setSegments(toSelectValues(segments))
      const types = await makeGetRequest('/v1/company-type')
      setCompanyTypes(toSelectValues(types))
    })()
  }, [])

  const onSelectCallback = params => {
    const { address, city, state, latitude, longitude, zipCode, country, street } = params
    setLocalLead(draft => {
      draft.address = address
      draft.city = city
      draft.state = state
      draft.latitude = latitude
      draft.longitude = longitude
      draft.zipCode = zipCode
      draft.country = country
      draft.street = street
    })
  }

  const validateProperty = (value, suggestions) => {
    const error = !!suggestions.filter(
      suggestion => suggestion.title.trim().toLowerCase() === value.trim().toLowerCase()
    ).length
    setHasDuplicateError(error)
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={isCreateCompanyModalOpen}
        toggle={toggleCreateCompanyModal}
        size="xl"
      >
        <ModalHeader toggle={toggleCreateCompanyModal} className={s.modalHeader}>
          Create Company from Lead
        </ModalHeader>
        <ModalBody>
          <Container>
            <p>
              {!isInfoSectionOpen ? (
                <FaAngleRight
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsInfoSectionOpen(!isInfoSectionOpen)}
                />
              ) : (
                <FaAngleDown
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsInfoSectionOpen(!isInfoSectionOpen)}
                />
              )}{' '}
              <span className={s.category}>Lead Information</span>
            </p>
            {isInfoSectionOpen && (
              <Row style={{ marginBottom: 30 }}>
                <Col>
                  <div className={s.leadInfo}>
                    <p>
                      <span className={s.leadInfoLabel}>First:</span>
                      <span className={s.leadInfoValue}>{lead.contactFirstName}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Last:</span>
                      <span className={s.leadInfoValue}>{lead.contactLastName}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Email:</span>
                      <span className={s.leadInfoValue}>{lead.email}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Phone:</span>
                      <span className={s.leadInfoValue}>{lead.phone}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Title:</span>
                      <span className={s.leadInfoValue}>{lead.title}</span>
                    </p>
                    <p>
                      <span className={s.leadInfoLabel}>Company:</span>
                      <span className={s.leadInfoValue}>{lead.company}</span>
                    </p>
                  </div>
                </Col>
                <Col>
                  <div className={s.leadInfo}>
                    <div>
                      <span className={s.leadInfoLabel}>Address</span>
                      <p className={s.leadInfoValue}>{lead.address}</p>
                      <p className={s.leadInfoValue}>{lead.address2}</p>
                    </div>
                  </div>
                </Col>
                <div className={s.aboutInfo}>
                  <p>
                    <span className={s.leadInfoLabel}>About:</span>
                    <span className={s.leadInfoValue}>{lead.about}</span>
                  </p>
                </div>
              </Row>
            )}
            <p>
              {!isCreateCompanySectionOpen ? (
                <FaAngleRight
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsCreateCompanySectionOpen(!isCreateCompanySectionOpen)}
                />
              ) : (
                <FaAngleDown
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsCreateCompanySectionOpen(!isCreateCompanySectionOpen)}
                />
              )}{' '}
              <span className={s.category}>Create Company from Lead</span>
            </p>
            {isCreateCompanySectionOpen && (
              <>
                <Row>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: '* Company Name',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <>
                                {hasDuplicateError && (
                                  <p className={s.error}>
                                    Another company with the same name already exists
                                  </p>
                                )}
                                <AutosuggestInput
                                  type="COMPANY"
                                  placeholder="Enter Company Name"
                                  onChangeCb={(value, suggestions) => {
                                    setCompanyName(value)
                                    validateProperty(value, suggestions)
                                  }}
                                  length="3"
                                  initialValue={companyName}
                                />
                              </>
                            )
                          },
                          {
                            key: '* Company Legal Name',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Company Legal Name"
                                cb={setCompanyLegalName}
                                text={companyLegalName}
                              />
                            )
                          },
                          {
                            key: 'Phone',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Phone"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.phone = value
                                  })
                                }
                                text={localLead.phone}
                              />
                            )
                          },
                          {
                            key: 'Additional Phone',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Additional Phone"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.additionalPhone = value
                                  })
                                }
                                text={localLead.additionalPhone}
                              />
                            )
                          },
                          {
                            key: 'Fax',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Fax"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.fax = value
                                  })
                                }
                                text={localLead.fax}
                              />
                            )
                          },
                          {
                            key: 'Website',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="text"
                                placeholder="Enter Website"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.website = value
                                  })
                                }
                                text={localLead.website}
                              />
                            )
                          },
                          {
                            key: 'Company Email',
                            keyClass: [s.label],
                            valueClass: [s.value],
                            value: (
                              <InputContainer
                                type="email"
                                placeholder="Enter Company Email"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.email = value
                                  })
                                }
                                text={localLead.email}
                              />
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: 'Address',
                            value: (
                              <GoogleAutocomplete
                                value={localLead.address}
                                onChange={value =>
                                  setLocalLead(draft => {
                                    draft.address = value
                                  })
                                }
                                onSelectCallback={onSelectCallback}
                              />
                            )
                          },
                          {
                            key: 'Street Address',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.address2}
                                placeholder="Street 1"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.address2 = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'City',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.city}
                                placeholder="Enter City"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.city = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Region',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.region}
                                placeholder="Enter Region"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.region = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Zip/Postal Code',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.zipCode}
                                placeholder="Enter Zip/Postal Code"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.zipCode = value
                                  })
                                }
                              />
                            )
                          },
                          {
                            key: 'Country',
                            value: (
                              <InputContainer
                                type="text"
                                text={localLead.country}
                                placeholder="Enter Country"
                                cb={value =>
                                  setLocalLead(draft => {
                                    draft.country = value
                                  })
                                }
                              />
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: 'Company Type',
                            value: (
                              <Select
                                onChange={setTypes}
                                options={companyTypes}
                                value={types}
                                isMulti
                              />
                            )
                          },
                          {
                            key: 'Company Level',
                            value: (
                              <Select
                                onChange={setSize}
                                options={fromConstantPropertyToSelectOptions(
                                  Object.keys(APP_CONSTANTS.CompanySize),
                                  'CompanySize'
                                )}
                                value={size}
                              />
                            )
                          },
                          {
                            key: 'Pre-Qualified',
                            value: (
                              <div className={s.radioGroup}>
                                <input
                                  type="radio"
                                  checked={preQualified}
                                  onChange={() => setPreQualified(true)}
                                />{' '}
                                Yes
                                <input
                                  type="radio"
                                  checked={!preQualified}
                                  onChange={() => {
                                    setPreQualified(false)
                                  }}
                                  className={s.rightRadio}
                                />{' '}
                                No
                              </div>
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                  <Col>
                    <div className={s.leadInfo}>
                      <KeyValuePairs
                        pairs={[
                          {
                            key: 'Market Segment',
                            value: (
                              <Select onChange={setSegment} options={segments} value={segment} />
                            )
                          },
                          {
                            key: 'Parent Company',
                            value: (
                              <PaginatedSelect
                                url="/v1/companies"
                                value={parentCompany}
                                onChange={setParentCompany}
                                serializer={toCompaniesSelect}
                              />
                            )
                          },
                          {
                            key: 'Pre-Qualification Exp Date',
                            value: (
                              <DatePicker
                                value={preQualifiedExpDate && timezoneDate(preQualifiedExpDate)}
                                onChange={value => {
                                  const date = value && value !== null ? utcDate(value) : ''
                                  setPreQualifiedExpDate(date)
                                }}
                              />
                            )
                          }
                        ]}
                      />
                    </div>
                  </Col>
                </Row>
                <KeyValuePairs
                  style={{ padding: '0 30px' }}
                  pairs={[
                    {
                      key: 'Company Description',
                      keyClass: [s.aboutLabel],
                      valueClass: [s.valueLabel],
                      value: (
                        <InputContainer
                          type="textarea"
                          classname={s.aboutInput}
                          placeholder="Enter company description"
                          cb={setCompanyDescription}
                          text={companyDescription}
                        />
                      )
                    }
                  ]}
                />
                <div className={s.checkboxWrapper}>
                  <Checkbox
                    isChecked={shouldCopyNotes}
                    triggerCheckbox={value => setShouldCopyNotes(value.target.checked)}
                  />
                  <p>Copy Lead Notes to Company Notes</p>
                  <br />
                  <Checkbox
                    isChecked={shouldCopyChecklist}
                    triggerCheckbox={value => setShouldCopyChecklist(value.target.checked)}
                  />
                  <p>Copy Lead Checklists to Company Checklists</p>
                  <br />
                  <Checkbox
                    isChecked={shouldCopySpend}
                    triggerCheckbox={value => setShouldCopySpend(value.target.checked)}
                  />
                  <p>Copy Lead Spend to Company Spend</p>
                </div>
              </>
            )}
          </Container>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCreateCompanyModal}>
            Cancel
          </Button>
          <Button
            className={s.saveBtn}
            onClick={() => {
              createCompany(
                {
                  ...localLead,
                  name: companyName,
                  legalName: companyLegalName,
                  companyDescription,
                  companyTypeIds: types.map(type => type.value),
                  size: size?.value ?? null,
                  marketSegmentId: segment?.value ?? null,
                  parentCompanyId: parentCompany?.value ?? null,
                  preQualified,
                  preQualificationExpDate: preQualifiedExpDate
                },
                {
                  shouldCopyChecklist,
                  shouldCopyNotes,
                  shouldCopySpend
                }
              )
            }}
            disabled={!companyName || !companyLegalName}
          >
            Create Company
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreateCompanyModal
